import { useEffect } from '@adornis/functional-lit/stateful.js';
import * as m from '../../../localization/out/messages.js';

/**
 * Translate a `ValidationError` / `MultipleValidationError` (or any other
 * thrown error) into a single user-facing string suitable for a snackbar or
 * alert. Mirrors what `@adornis/generated-form/render-field.ts` does inline:
 * looks up the paraglide template for `extensions.translationKey` (e.g.
 * `validation_non_optional`) and substitutes `{key}` with the field's
 * translated label (`<labelPrefix><fieldKey>_label`).
 *
 * Without this helper, callers usually do `setSaveError(err.message ?? '…')`,
 * which surfaces "Multiple ValidationErrors were thrown during validation: …"
 * for multi-field errors and `Wert "undefined" ist erforderlich!` when the
 * label key is missing.
 *
 * @param err  the caught error
 * @param labelPrefix string prepended to the leaf field key to find its
 *   label in the paraglide messages. For a flat form pass its
 *   `translationPrefix` (e.g. `'job_form_'`); for a nested form whose
 *   fields live one level deeper, include the path segment too (e.g.
 *   `'motivation_form_motivations_'`).
 */
export const formatFormError = (err: any, labelPrefix = ''): string => {
  const errors: any[] = err?.validationErrors ?? [err];
  const lines = errors
    .map(e => {
      const translationKey: string | undefined = e?.extensions?.translationKey;
      const fieldKey: string | undefined = e?.extensions?.key;
      const labelMessage = fieldKey ? (m as any)[`${labelPrefix}${fieldKey}_label`] : undefined;
      const fieldLabel = labelMessage ? labelMessage() : fieldKey;
      const tmpl = translationKey ? (m as any)[translationKey] : undefined;
      if (tmpl) return tmpl({ key: fieldLabel ?? fieldKey ?? '' });
      return e?.message;
    })
    .filter(Boolean);
  if (lines.length === 0) return m.form_error_fallback();
  // De-dupe — N empty rows of the same field produce N identical errors;
  // collapse them so the user sees one line per distinct issue.
  return Array.from(new Set(lines)).join(' • ');
};

/**
 * When `error` transitions to truthy, smooth-scroll the matching error-anchor
 * element into view. Use on long forms where the user may have scrolled past
 * the banner before hitting submit.
 *
 * `anchorName` scopes the query to `[data-error-anchor="<name>"]` — needed on
 * pages with multiple banners that could each surface their own error. If
 * omitted, matches the first `[data-error-anchor]` in document order.
 */
export const useScrollToError = (error: unknown, anchorName?: string) => {
  useEffect(() => {
    if (!error) return;
    // Wait for the alert to be committed to the DOM before scrolling.
    const selector = anchorName ? `[data-error-anchor="${anchorName}"]` : '[data-error-anchor]';
    requestAnimationFrame(() => {
      // Multiple onboarding steps stay mounted (hidden) at once, so an unnamed selector can
      // match an anchor inside a hidden step - scroll to the first actually visible one.
      const el = Array.from(document.querySelectorAll<HTMLElement>(selector)).find(
        candidate => candidate.offsetParent !== null,
      );
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [error]);
};
